import React from 'react';
import Api from '../../api';
import { trackCeDirectoryEvent } from './analytics';
import CeDirectoryCard from './CeDirectoryCard';
import {
  FORMAT_LABELS,
  getScopeLabel,
  GROUP_LABELS,
} from './display';
import { getCeDirectoryErrorCode } from './errors';
import { getLocationContextInvalidEvent } from './locationContext';
import {
  publishCeDirectoryAvailability,
  useCeDirectoryAvailability,
} from './runtimeAvailability';
import {
  readCeDirectoryFilters,
  writeCeDirectoryFilters,
} from './storage';
import type {
  CeDirectoryFilters,
  CeDirectoryProps,
  CeDirectoryResponse,
  CeDirectoryScope,
} from './types';
import styles from './CeDirectory.module.scss';

const GROUP_OPTIONS = Object.entries(GROUP_LABELS) as Array<[CeDirectoryFilters['group'], string]>;
const FORMAT_OPTIONS = Object.entries(FORMAT_LABELS) as Array<[CeDirectoryFilters['format'], string]>;
const SCOPE_OPTIONS: CeDirectoryScope[] = ['city', 'province_state', 'country', 'north_america'];

function expectedAudienceMatches(sourceSurface: CeDirectoryProps['sourceSurface'], audience: string): boolean {
  return sourceSurface === 'talent'
    ? audience === 'hygienist' || audience === 'assistant'
    : audience === 'dental_office';
}

function nextWiderScope(scope: CeDirectoryScope): CeDirectoryScope | null {
  if (scope === 'city') return 'province_state';
  if (scope === 'province_state') return 'country';
  if (scope === 'country') return 'north_america';
  return null;
}

const CeDirectory: React.FC<CeDirectoryProps> = ({
  userId,
  sourceSurface,
  locationId,
  onAvailabilityChange,
  onLocationContextInvalid,
}) => {
  const enabled = useCeDirectoryAvailability(userId);
  const [filters, setFilters] = React.useState<CeDirectoryFilters>(() => readCeDirectoryFilters(userId));
  const [filterOwnerId, setFilterOwnerId] = React.useState(String(userId));
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [listings, setListings] = React.useState<CeDirectoryResponse['listings']>([]);
  const [meta, setMeta] = React.useState<CeDirectoryResponse['meta'] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [initialError, setInitialError] = React.useState<string | null>(null);
  const [nextPageLoading, setNextPageLoading] = React.useState(false);
  const [nextPageError, setNextPageError] = React.useState<string | null>(null);
  const [retryToken, setRetryToken] = React.useState(0);
  const requestVersion = React.useRef(0);
  const pageController = React.useRef<AbortController | null>(null);
  const didTrackSearch = React.useRef(false);
  const previousUserId = React.useRef(String(userId));
  const onAvailabilityChangeRef = React.useRef(onAvailabilityChange);
  const onLocationContextInvalidRef = React.useRef(onLocationContextInvalid);

  React.useEffect(() => {
    onAvailabilityChangeRef.current = onAvailabilityChange;
  }, [onAvailabilityChange]);

  React.useEffect(() => {
    onLocationContextInvalidRef.current = onLocationContextInvalid;
  }, [onLocationContextInvalid]);

  React.useEffect(() => {
    if (enabled !== null) onAvailabilityChangeRef.current?.(enabled);
  }, [enabled]);

  React.useEffect(() => {
    if (previousUserId.current === String(userId)) return;
    previousUserId.current = String(userId);
    setFilterOwnerId(String(userId));
    setFilters(readCeDirectoryFilters(userId));
    setSearch('');
    setDebouncedSearch('');
    setListings([]);
    setMeta(null);
    didTrackSearch.current = false;
  }, [userId]);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    if (!didTrackSearch.current) {
      didTrackSearch.current = true;
      return;
    }
    trackCeDirectoryEvent('CE Directory Filter Changed', filters, {
      effectiveScope: meta?.scope.effective,
    });
  // Track only the debounced search action. Search text is deliberately absent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  React.useEffect(() => {
    if (enabled !== true || filterOwnerId !== String(userId)) return undefined;
    const controller = new AbortController();
    pageController.current?.abort();
    pageController.current = null;
    setNextPageLoading(false);
    const version = ++requestVersion.current;
    setLoading(true);
    setInitialError(null);
    setNextPageError(null);
    setListings([]);
    setMeta(null);

    void Api.getCeDirectoryListings({
      ...filters,
      q: debouncedSearch || undefined,
      page: 1,
      locationId,
    }, controller.signal).then(response => {
      if (controller.signal.aborted || version !== requestVersion.current) return;
      if (!expectedAudienceMatches(sourceSurface, response.meta.audience)) {
        throw new Error('The directory returned an unexpected member audience.');
      }
      setListings(response.listings);
      setMeta(response.meta);
      trackCeDirectoryEvent('CE Directory Loaded', filters, {
        effectiveScope: response.meta.scope.effective,
      });
    }).catch(error => {
      if (controller.signal.aborted || version !== requestVersion.current) return;
      const invalidLocation = getLocationContextInvalidEvent(error, sourceSurface);
      if (invalidLocation) onLocationContextInvalidRef.current?.(invalidLocation);
      if (getCeDirectoryErrorCode(error) === 'CE_DIRECTORY_NOT_AVAILABLE') {
        publishCeDirectoryAvailability(userId, false);
        onAvailabilityChangeRef.current?.(false);
        return;
      }
      setInitialError(sourceSurface === 'talent'
        ? 'CE courses are temporarily unavailable. Your TempStars courses are still available above.'
        : 'CE courses are temporarily unavailable. Please try again.');
    }).finally(() => {
      if (!controller.signal.aborted && version === requestVersion.current) setLoading(false);
    });

    return () => controller.abort();
  }, [
    debouncedSearch,
    enabled,
    filterOwnerId,
    filters,
    locationId,
    retryToken,
    sourceSurface,
    userId,
  ]);

  React.useEffect(() => () => pageController.current?.abort(), []);

  const updateFilter = <K extends keyof CeDirectoryFilters>(key: K, value: CeDirectoryFilters[K]) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    writeCeDirectoryFilters(userId, next);
    trackCeDirectoryEvent('CE Directory Filter Changed', next, {
      effectiveScope: meta?.scope.effective,
    });
  };

  const loadNextPage = React.useCallback(async () => {
    if (!meta?.pagination.hasNextPage || nextPageLoading) return;
    pageController.current?.abort();
    const controller = new AbortController();
    pageController.current = controller;
    const version = requestVersion.current;
    const page = meta.pagination.page + 1;
    setNextPageLoading(true);
    setNextPageError(null);
    try {
      const response = await Api.getCeDirectoryListings({
        ...filters,
        q: debouncedSearch || undefined,
        page,
        locationId,
      }, controller.signal);
      if (controller.signal.aborted || version !== requestVersion.current) return;
      if (!expectedAudienceMatches(sourceSurface, response.meta.audience)) {
        throw new Error('The directory returned an unexpected member audience.');
      }
      setListings(current => {
        const seen = new Set(current.map(listing => listing.id));
        return [...current, ...response.listings.filter(listing => !seen.has(listing.id))];
      });
      setMeta(response.meta);
      trackCeDirectoryEvent('CE Directory Page Loaded', filters, {
        effectiveScope: response.meta.scope.effective,
      });
    } catch (error) {
      if (controller.signal.aborted || version !== requestVersion.current) return;
      const invalidLocation = getLocationContextInvalidEvent(error, sourceSurface);
      if (invalidLocation) onLocationContextInvalidRef.current?.(invalidLocation);
      if (getCeDirectoryErrorCode(error) === 'CE_DIRECTORY_NOT_AVAILABLE') {
        publishCeDirectoryAvailability(userId, false);
        onAvailabilityChangeRef.current?.(false);
        return;
      }
      setNextPageError('We could not load more courses. Please try again.');
    } finally {
      if (!controller.signal.aborted && version === requestVersion.current) setNextPageLoading(false);
    }
  }, [
    debouncedSearch,
    filters,
    locationId,
    meta,
    nextPageLoading,
    sourceSurface,
    userId,
  ]);

  if (enabled !== true) return null;

  const countryCode = meta?.scope.context.countryCode;
  const widerScope = nextWiderScope(filters.scope);
  const effectiveScope = meta?.scope.effective ?? filters.scope;

  return (
    <section className={styles.directory} aria-labelledby="ce-directory-heading">
      <div className={styles.headingRow}>
        <div>
          <h2 id="ce-directory-heading">Explore CE courses</h2>
          <p>Discover courses from independent continuing-education providers.</p>
        </div>
        {meta && <span className={styles.total}>{meta.pagination.total} courses</span>}
      </div>

      <div className={styles.controls}>
        <label className={styles.searchControl}>
          <span>Search</span>
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search courses or providers"
            maxLength={200}
          />
        </label>
        <label>
          <span>Group</span>
          <select
            value={filters.group}
            onChange={event => updateFilter('group', event.target.value as CeDirectoryFilters['group'])}
          >
            {GROUP_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>Format</span>
          <select
            value={filters.format}
            onChange={event => updateFilter('format', event.target.value as CeDirectoryFilters['format'])}
          >
            {FORMAT_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>Location</span>
          <select
            value={filters.scope}
            onChange={event => updateFilter('scope', event.target.value as CeDirectoryScope)}
          >
            {SCOPE_OPTIONS.map(scope => (
              <option
                value={scope}
                key={scope}
                disabled={scope === 'city' && meta?.scope.cityAvailable === false}
              >
                {getScopeLabel(scope, countryCode)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className={styles.helperText}>
        Location affects in-person courses. Online and on-demand courses are always included.
      </p>

      {meta?.scope.fallbackApplied && (
        <div className={styles.fallbackNotice} role="status">
          {getScopeLabel(meta.scope.requested, countryCode)} is unavailable for this account, so results use {getScopeLabel(meta.scope.effective, countryCode).toLowerCase()} instead.
        </div>
      )}

      {loading && (
        <div className={styles.loadingState} role="status" aria-live="polite">
          <span className={styles.spinner} aria-hidden="true" />
          Loading CE courses…
        </div>
      )}

      {!loading && initialError && (
        <div className={styles.errorState} role="alert">
          <strong>Unable to load CE courses</strong>
          <p>{initialError}</p>
          <button type="button" onClick={() => setRetryToken(value => value + 1)}>Retry</button>
        </div>
      )}

      {!loading && !initialError && listings.length === 0 && (
        <div className={styles.emptyState}>
          <strong>No CE courses match these filters</strong>
          <p>Try a different search or broaden your location.</p>
          {widerScope && (
            <button type="button" onClick={() => updateFilter('scope', widerScope)}>
              Widen to {getScopeLabel(widerScope, countryCode)}
            </button>
          )}
        </div>
      )}

      {listings.length > 0 && (
        <div className={styles.results} aria-live="polite">
          {listings.map(listing => (
            <CeDirectoryCard
              key={listing.id}
              listing={listing}
              filters={filters}
              effectiveScope={effectiveScope}
              sourceSurface={sourceSurface}
              locationId={locationId}
            />
          ))}
        </div>
      )}

      {nextPageError && (
        <div className={styles.nextPageError} role="alert">
          <span>{nextPageError}</span>
          <button type="button" onClick={loadNextPage}>Retry</button>
        </div>
      )}
      {meta?.pagination.hasNextPage && !nextPageError && (
        <button
          type="button"
          className={styles.loadMoreButton}
          onClick={loadNextPage}
          disabled={nextPageLoading}
        >
          {nextPageLoading ? 'Loading…' : 'Load more courses'}
        </button>
      )}
    </section>
  );
};

export default CeDirectory;
